<template>
	<div class="manufacturer-archive">
		<!-- Filter Bar -->
		<div class="manufacturer-filters">
			<div class="container">
				<div class="row g-3">
					<!-- Search -->
					<div class="col-12 col-md">
						<div class="manufacturer-search">
							<i class="bi bi-search"></i>
							<input
								v-model="search"
								type="text"
								class="form-control"
								placeholder="Search"
								@input="debouncedFetch"
							/>
							<button
								v-if="search"
								@click="clearSearch"
								class="manufacturer-search__clear"
								aria-label="Clear search"
							>
								<i class="bi bi-x-circle"></i>
							</button>
						</div>
					</div>

					<!-- All taxonomies mode (archive page) -->
					<div v-if="!scopedTaxonomy" class="col-12 col-md-7">
						<div class="row g-3">
							<div class="col-md-4">
								<select
									v-model="filters.residential_flooring"
									class="form-select"
									@change="onFilterChange('residential_flooring')"
								>
									<option value="">All Residential Flooring</option>
									<option
										v-for="term in terms.residential_flooring"
										:key="term.slug"
										:value="term.slug"
									>
										{{ term.name }}
									</option>
								</select>
							</div>
							<div class="col-md-4">
								<select
									v-model="filters.commercial_flooring"
									class="form-select"
									@change="onFilterChange('commercial_flooring')"
								>
									<option value="">All Commercial Flooring</option>
									<option
										v-for="term in terms.commercial_flooring"
										:key="term.slug"
										:value="term.slug"
									>
										{{ term.name }}
									</option>
								</select>
							</div>
							<div class="col-md-4">
								<select
									v-model="filters.surfacing_products"
									class="form-select"
									@change="onFilterChange('surfacing_products')"
								>
									<option value="">All Surfacing Products</option>
									<option
										v-for="term in terms.surfacing_products"
										:key="term.slug"
										:value="term.slug"
									>
										{{ term.name }}
									</option>
								</select>
							</div>
						</div>
					</div>

					<!-- Single taxonomy mode (template pages) -->
					<div v-else class="col col-md-4">
						<select
							v-model="filters[scopedTaxonomy]"
							class="form-select"
							@change="fetchManufacturers"
						>
							<option value="">All {{ taxonomyLabels[scopedTaxonomy] }}</option>
							<option
								v-for="term in terms[scopedTaxonomy]"
								:key="term.slug"
								:value="term.slug"
							>
								{{ term.name }}
							</option>
						</select>
					</div>
				</div>
			</div>
		</div>

		<!-- Loading -->
		<div v-if="loading" class="manufacturer-loading">
			<div class="spinner-border" role="status">
				<span class="visually-hidden">Loading...</span>
			</div>
		</div>

		<!-- Error -->
		<div v-else-if="error" class="manufacturer-error container">
			<div class="alert alert-danger">{{ error }}</div>
		</div>

		<!-- Grid -->
		<div v-else class="container">
			<p class="manufacturer-count">Showing {{ manufacturers.length }} products</p>
			<div class="row g-4">
				<div
					v-for="m in manufacturers"
					:key="m.id"
					class="col-12 col-sm-6 col-md-4 col-lg-3"
				>
					<ManufacturerCard :manufacturer="m" @click="openModal" />
				</div>
			</div>

			<div v-if="!loading && manufacturers.length === 0" class="manufacturer-empty">
				<i class="bi bi-search"></i>
				<h3>No manufacturers found</h3>
				<p>Try adjusting your search or filters</p>
				<button @click="clearAll" class="btn btn-primary">Clear Filters</button>
			</div>
		</div>

		<!-- Modal -->
		<ManufacturerModal
			:manufacturer="selectedManufacturer"
			:isOpen="isModalOpen"
			@close="closeModal"
		/>
	</div>
</template>

<script>
import { ref, reactive, computed, onMounted } from 'vue';
import ManufacturerCard from './ManufacturerCard.vue';
import ManufacturerModal from './ManufacturerModal.vue';

export default {
	name: 'ManufacturerArchive',
	components: { ManufacturerCard, ManufacturerModal },
	setup() {
		const manufacturers = ref([]);
		const loading = ref(true);
		const error = ref(null);
		const search = ref('');
		const selectedManufacturer = ref(null);
		const isModalOpen = ref(false);

		// Read scoped taxonomy from data attribute (set on template pages)
		const container = document.getElementById('manufacturer-app');
		const scopedTaxonomy = ref(container?.dataset.taxonomy || null);

		const filters = reactive({
			residential_flooring: '',
			commercial_flooring: '',
			surfacing_products: '',
		});

		const terms = reactive({
			residential_flooring: [],
			commercial_flooring: [],
			surfacing_products: [],
		});

		const taxonomyLabels = {
			residential_flooring: 'Residential Flooring',
			commercial_flooring: 'Commercial Flooring',
			surfacing_products: 'Surfacing Products',
		};

		let debounceTimer = null;
		const ajaxUrl = window.jaeckleData?.ajaxUrl || '/wp-admin/admin-ajax.php';

		const hasActiveFilters = computed(() => {
			if (search.value) return true;
			return Object.values(filters).some(v => v !== '');
		});

		const getTermName = (taxonomy, slug) => {
			const term = terms[taxonomy]?.find(t => t.slug === slug);
			return term ? term.name : slug;
		};

		const fetchTerms = async () => {
			try {
				const params = new URLSearchParams({ action: 'get_manufacturer_terms' });
				const response = await fetch(`${ajaxUrl}?${params}`);
				if (!response.ok) return;

				const data = await response.json();
				if (data.success) {
					terms.residential_flooring = data.data.residential_flooring || [];
					terms.commercial_flooring = data.data.commercial_flooring || [];
					terms.surfacing_products = data.data.surfacing_products || [];
				}
			} catch (err) {
				console.error('Error fetching terms:', err);
			}
		};

		const fetchManufacturers = async () => {
			loading.value = true;
			error.value = null;

			try {
				const params = new URLSearchParams({ action: 'get_manufacturers' });

				if (search.value) params.set('search', search.value);
				if (scopedTaxonomy.value) params.set('scope', scopedTaxonomy.value);

				Object.entries(filters).forEach(([taxonomy, value]) => {
					if (value) {
						params.append(`filters[${taxonomy}]`, value);
					}
				});

				const response = await fetch(`${ajaxUrl}?${params}`);
				if (!response.ok) throw new Error('Failed to fetch manufacturers');

				const data = await response.json();
				if (data.success) {
					manufacturers.value = data.data.manufacturers;
				} else {
					throw new Error(data.data || 'Failed to fetch');
				}
			} catch (err) {
				error.value = err.message;
			} finally {
				loading.value = false;
			}
		};

		const debouncedFetch = () => {
			clearTimeout(debounceTimer);
			debounceTimer = setTimeout(fetchManufacturers, 400);
		};

		const clearSearch = () => {
			search.value = '';
			fetchManufacturers();
		};

		const clearFilter = taxonomy => {
			filters[taxonomy] = '';
			fetchManufacturers();
		};

		// When a filter is selected, clear all other filters (mutually exclusive)
		const onFilterChange = selectedTaxonomy => {
			// Clear all other filters
			Object.keys(filters).forEach(taxonomy => {
				if (taxonomy !== selectedTaxonomy) {
					filters[taxonomy] = '';
				}
			});
			fetchManufacturers();
		};

		const clearAll = () => {
			search.value = '';
			filters.residential_flooring = '';
			filters.commercial_flooring = '';
			filters.surfacing_products = '';
			fetchManufacturers();
		};

		const openModal = manufacturer => {
			selectedManufacturer.value = manufacturer;
			isModalOpen.value = true;
		};

		const closeModal = () => {
			isModalOpen.value = false;
			setTimeout(() => {
				selectedManufacturer.value = null;
			}, 300);
		};

		onMounted(async () => {
			await fetchTerms();
			fetchManufacturers();
		});

		return {
			manufacturers,
			loading,
			error,
			search,
			filters,
			terms,
			taxonomyLabels,
			scopedTaxonomy,
			hasActiveFilters,
			selectedManufacturer,
			isModalOpen,
			fetchManufacturers,
			debouncedFetch,
			getTermName,
			clearSearch,
			clearFilter,
			clearAll,
			onFilterChange,
			openModal,
			closeModal,
		};
	},
};
</script>
